import {
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetReviewQueue } from "@/accounts-payable/query";
import type { ApReviewQueueReturnPosition } from "@/accounts-payable/types";
import {
  AP_TAB,
  flattenPages,
  invoiceReviewPath,
} from "@/accounts-payable/utils";
import {
  getQueueReturnIndex,
  needsMoreQueuePages,
  readQueueReturnPosition,
} from "./utils";

export const useReviewQueueNavigation = (businessId: string | null) => {
  const location = useLocation();
  const navigate = useNavigate();
  const listPath = location.pathname + location.search + location.hash;
  const savedPosition = readQueueReturnPosition(location.state, businessId);
  const position = savedPosition?.listPath === listPath ? savedPosition : null;
  const [restored, setRestored] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const queue = useGetReviewQueue(businessId, !!position);
  const items = useMemo(() => flattenPages(queue.data?.pages), [queue.data]);
  const loadedPageCount = queue.data?.pages.length ?? 0;
  const needsMorePages = position
    ? needsMoreQueuePages({
        position,
        items,
        loadedPageCount,
        hasNextPage: queue.hasNextPage,
      })
    : false;
  const restoring = !!position && !restored;
  const awaitingPosition =
    restoring &&
    !queue.isError &&
    (queue.isPending || queue.isFetching || needsMorePages);
  const { fetchNextPage } = queue;

  // A reload or an expired cache may have kept only the first page. Fetch the
  // saved depth before attempting to scroll; otherwise the browser clamps it.
  useEffect(() => {
    if (restoring && needsMorePages && !queue.isFetching && !queue.isError) {
      void fetchNextPage();
    }
  }, [
    restoring,
    needsMorePages,
    loadedPageCount,
    queue.isFetching,
    queue.isError,
    fetchNextPage,
  ]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (
      !position ||
      !restoring ||
      awaitingPosition ||
      queue.isError ||
      !viewport
    )
      return;
    const index = getQueueReturnIndex(
      position,
      items.map((item) => item.id),
    );
    const card = viewport.querySelectorAll<HTMLElement>(
      "[data-review-invoice-id]",
    )[index];
    const scrollTop = card
      ? viewport.scrollTop +
        card.getBoundingClientRect().top -
        viewport.getBoundingClientRect().top -
        position.offset
      : position.scrollTop;
    // A page can contain the anchor without enough content below it to reach
    // the saved offset. Load that content before the browser clamps scrolling.
    if (
      scrollTop > viewport.scrollHeight - viewport.clientHeight + 1 &&
      queue.hasNextPage
    ) {
      void fetchNextPage();
      return;
    }
    viewport.scrollTop = scrollTop;
    setRestored(true);
  }, [
    position,
    restoring,
    awaitingPosition,
    queue.isError,
    queue.hasNextPage,
    fetchNextPage,
    items,
  ]);

  const openInvoice = (
    invoiceId: string,
    event: MouseEvent<HTMLAnchorElement>,
  ) => {
    // Keep the link's normal new-tab and modifier-key behavior.
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const viewport = viewportRef.current;
    const card = event.currentTarget.closest<HTMLElement>(
      "[data-review-invoice-id]",
    );
    const invoiceIndex = items.findIndex((item) => item.id === invoiceId);
    const invoice = items[invoiceIndex];
    if (!businessId || !viewport || !card || !invoice) return;
    const position: ApReviewQueueReturnPosition = {
      businessId,
      listPath,
      invoiceIds: items.map((item) => item.id),
      invoiceIndex,
      invoiceCreatedAt: invoice.created_at,
      offset:
        card.getBoundingClientRect().top - viewport.getBoundingClientRect().top,
      scrollTop: viewport.scrollTop,
      pageCount: loadedPageCount,
    };
    event.preventDefault();
    navigate(invoiceReviewPath(invoiceId, AP_TAB.review), {
      state: { queueReturnPosition: position },
    });
  };

  return {
    queue,
    items,
    viewportRef,
    awaitingPosition,
    openInvoice,
    restoring,
  };
};
